import _ from 'underscore';

import {
  allEnumValues,
  MenuItemDisplayStyle,
  MenuSectionDesignation,
  MenuSectionHeaderType,
  MenuSectionHeaderTypeHelper,
  MenuSectionPriceDisplay,
  ModelType,
  QuickAddEligibility,
  QuickAddEligibilityHelper,
} from '@biteinc/enums';
import { StringHelper } from '@biteinc/helpers';
import { menuSectionSchema } from '@biteinc/schemas';

app.MenuSection = app.MenuItemArray.extend({
  ModelName: 'menuSection',
  Schema: menuSectionSchema,
  Type: ModelType.MenuSection,

  defaults() {
    const defaults = {
      designation: MenuSectionDesignation.Regular,
      defaultItemDisplayStyle: MenuItemDisplayStyle.NameAndImage,
      priceDisplay: MenuSectionPriceDisplay.Default,
      items: [],
    };
    if (app.vendorList && app.vendorList.hasBeenFetched() && app.vendorList.size()) {
      const vendor = app.vendorList.isEveryVendorIntegrated()
        ? app.vendorList.getDefaultVendorOrFallback()
        : app.vendorList.getDefaultNonintegratedVendor();
      defaults.vendorId = vendor.id;

      if (vendor.syncsModelsWithType(ModelType.MenuSection)) {
        defaults.designation = MenuSectionDesignation.OrderHistory;
      }
    }
    return defaults;
  },

  displayName() {
    const name = app.MenuItemArray.prototype.displayName.apply(this, arguments);
    if (app.location.supportsMultipleVendors()) {
      const vendor = this.getFullVendor();
      return `${name}  (${vendor.displayName()})`;
    }
    return name;
  },

  // override menuItemArray super method
  updateItemEventListeners() {},

  displayNameHtml() {
    let title = `<span>${this.displayName()}</span>`;

    if (this.isMissingImages()) {
      title +=
        '&nbsp;&nbsp;<img class="badge-img" src="https://static.bureau.getbite.com/images/no-photo.png" ' +
        'srcset="https://static.bureau.getbite.com/images/no-photo@2x.png 2x" title="No Photos"/>';
    }

    if (this.get('activeTimings')?.length && app.menuTimingList) {
      this.get('activeTimings').forEach((timingRef) => {
        const menuTiming = app.menuTimingList.get(timingRef._id);
        if (menuTiming) {
          title += ` <span class="cell-badge active-timing" title="Show only during: ${menuTiming.displayName()}">🕙 ${menuTiming.get('name')}</span>`;
        }
      });
    }

    return title;
  },

  isMissingImages() {
    return !this.hasArr('images');
  },

  // This function let's us display the in element pictures of the display style
  // I don't know what madness happens in bureau
  displayNameForListFieldElement(field, element, subProperty, keyModel, plainTextOnly) {
    return plainTextOnly ? element.displayName() : element.displayNameHtml();
  },

  getVisibleInPlaces() {
    const places = [];
    app.menuStructureList.each((structure) => {
      const paths = structure.pathsToSectionWithId(this.id);
      places.push(...paths);
    });
    return places;
  },

  getFieldCollection(field, _subProperty, _includeAllValues, _keyModel) {
    switch (field) {
      case 'vendorId':
        if (this.getFullVendor().syncsModelsWithType(this.Type)) {
          // If the section is synced, then we only need the vendor of the section
          return app.vendorList.buildSingletonVendorList(this.get('vendorId'));
        }

        // If the section is unsynced, then we need the list of vendors that don't sync sections
        return app.vendorList.buildListOfVendorsThatDoNotSyncModelsWithType(this.Type);
      case 'designation':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuSectionDesignation,
          values: allEnumValues(MenuSectionDesignation),
          nameGenerator: (designation) => {
            switch (designation) {
              case MenuSectionDesignation.Regular:
                return 'Regular Section';
              case MenuSectionDesignation.RecommendationPane:
                return 'Recommendation Pane';
              case MenuSectionDesignation.QuizBanner:
                return 'Quiz Banner';
              case MenuSectionDesignation.OrderHistory:
                return 'Order History';
            }
          },
        });
      case 'eligibility':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: QuickAddEligibility,
          nameGenerator: QuickAddEligibilityHelper.name,
        });
      case 'activeTimings':
        return app.menuTimingList;
      case 'fulfillmentStationIds': {
        return app.AbstractCollection.createFromEnum({
          schema: app.site.get('fulfillmentStations').filter((station) => station.isEnabled),
          sort: app.AbstractCollection.SortOptions.NAME,
        });
      }
      case 'priceDisplay':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuSectionPriceDisplay,
          nameGenerator: (priceDisplay) => {
            switch (priceDisplay) {
              case MenuSectionPriceDisplay.Default:
                return 'Show prices';
              case MenuSectionPriceDisplay.HidePriceOptions:
                return 'Hide price options';
              default:
                throw new Error(`unrecognized priceDisplay value: ${priceDisplay}`);
            }
          },
        });
      case 'headerType':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuSectionHeaderType,
          nameGenerator: MenuSectionHeaderTypeHelper.name,
        });
      case 'defaultItemDisplayStyle': {
        const Model = app.AbstractModel.extend({
          displayNameHtml() {
            const displayName = this.displayName();
            switch (this.id) {
              case MenuItemDisplayStyle.NameAndDescription:
                return `<img class="dropdown-img" src="https://static.bureau.getbite.com/images/display-style-plain.png"/>${displayName}`;
              case MenuItemDisplayStyle.NameAndImage:
                return `<img class="dropdown-img" src="https://static.bureau.getbite.com/images/display-style-plain-pic.png"/>${displayName}`;
              case MenuItemDisplayStyle.Wide:
                return `<img class="dropdown-img" src="https://static.bureau.getbite.com/images/display-style-horizontal.png"/>${displayName}`;
              case MenuItemDisplayStyle.NameDescriptionAndImage:
                return `<img class="dropdown-img" src="https://static.bureau.getbite.com/images/display-style-plain-pic-description.png"/>${displayName}`;
              default:
                throw new Error(`unrecognized displayStyle value: ${this.id}`);
            }
          },
        });
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: MenuItemDisplayStyle,
          ModelClass: Model,
          nameGenerator: (displayStyle) => {
            switch (displayStyle) {
              case MenuItemDisplayStyle.NameAndDescription:
                return 'Name/Description';
              case MenuItemDisplayStyle.NameAndImage:
                return 'Name/Image';
              case MenuItemDisplayStyle.Wide:
                return 'Horizontal (with Image)';
              case MenuItemDisplayStyle.NameDescriptionAndImage:
                return 'Name/Description/Image';
              default:
                throw new Error(`unrecognized displayStyle value: ${displayStyle}`);
            }
          },
        });
      }
    }
    return null;
  },

  fieldIsPermanent(field /* subProperty */) {
    if (app.AbstractModel.prototype.fieldIsPermanent.apply(this, arguments)) {
      return true;
    }

    if (field === 'vendorId' && this.getFullVendor().syncsModelsWithType(this.Type)) {
      return true;
    }

    return false;
  },

  fieldIsHidden(field /* subProperty */) {
    if (app.AbstractModel.prototype.fieldIsHidden.apply(this, arguments)) {
      return true;
    }

    if (field === 'vendorId' && app.vendorList.isEveryVendorIntegrated()) {
      return true;
    }

    return false;
  },

  getListFieldElementAttributesFromModel(field, element) {
    return { _id: element.id };
  },

  subtitleForListFieldElement(field, element) {
    switch (field) {
      case 'items':
        return this.getItemDescription(element.id);
    }
    return '';
  },

  hasVisibleMenuItem(menuItem) {
    if (menuItem.isMod()) {
      return _.any(this.get('items') || [], (itemRef) => {
        const item = app.menuItemList.get(itemRef._id);
        return item.hasModWithId(menuItem.id);
      });
    }
    return _.contains(_.pluck(this.get('items'), '_id'), menuItem.id);
  },

  canBePrecisionCopied() {
    return true;
  },
});
